<script setup>
import { watch } from 'vue';

import { getAnalysisLogAPI } from '@/api/AnalysisApi';
import { DEV_MODE } from '@/config/dev-mode';

import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useAnalysisSajuContext, getHarmonyBaseId } from '@/composables/saju-insight/useAnalysisSajuContext';
import {
  useAnalysisServices,
  ANALYSIS_SERVICE_CONFIG,
} from '@/composables/saju-insight/useAnalysisServices';

/** 낭만궁합 */
import CoverPageHarmony from '@/components/saju-insight/nangman-harmony/CoverPageHarmony.vue';
import CoverPanelHarmony from '@/components/saju-insight/nangman-harmony/CoverPanelHarmony.vue';
import DetailPageHarmony from '@/components/saju-insight/nangman-harmony/DetailPageHarmony.vue';
import DetailPanelHarmony from '@/components/saju-insight/nangman-harmony/DetailPanelHarmony.vue';
/** 공통 */
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AlertComponent from '@/components/common/Alert.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';


const route = useRoute();
const router = useRouter();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const props = defineProps({ saju: { type: Object, required: true } });
const context = useAnalysisSajuContext();

const { selectedMenu } = storeToRefs(store);

const {
  serviceData,
  activeServiceKey,
  activeContent: analysisContent,
  activeTicketData: analysisTicketData,
  activeTicketCount: analysisTicketCount,
  fetchAllContents,
  getTypeIdByMenu,
  isWithinFreePageLimit: tendencyGuide,
  hasPermissionByTypeId,
} = useAnalysisServices();

const componentKey = ref(0);

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

const toggleTabOpenClass = ref('');
const toggleTabContent = () => {
  toggleTabOpenClass.value = toggleTabOpenClass.value === '' ? 'wide' : '';
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};
const handleChargePop = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
  showPopAnalysisInfo.value = false;
};
const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};
const handleChargePopOtherProduct = () => {
  router.push('/purchase/tickets');
  showPopAnalysisInfoOther.value = false;
};

const showPopAnalysisInfoDo = async () => {
  if (!analysisContent.value) {
    DEV_MODE && console.warn('analysisContent가 아직 로드되지 않았습니다.');
    return;
  }

  if (hasPermissionByTypeId(analysisContent.value.analysisTypeId)) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  if (
    paymentsStore?.points < analysisContent.value?.pointPrice &&
    analysisTicketCount.value === 0
  ) {
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

const dialogAlert = ref(null);
const dialogTitle = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');

const showAlert = ({ title, nextUrl }) => {
  dialogTitle.value = title;
  dialogText.value = '';
  dialogAlert.value.openAlertDialog();
  dialogNextUrl.value = nextUrl;
};

/** 상세분석 컨텐츠 불러오기 상태 */
const loadingPersonalityInfo = ref(true);

/** 상세분석 이용내역 정보 불러오기 */
const fetchAnalysisLogData = async (analysisTypeId = null) => {
  try {
    // 상세분석 URL의 사주로 구매 이력 범위를 제한한다.
    await store.getAnalysisLog(props.saju.guestUserId, analysisTypeId);
  } catch (e) {
    DEV_MODE && console.error(`낭만궁합 > 이용내역 로드 실패: ${JSON.stringify(e)}`);
  }
};

/** 포인트 및 분석권 정보 불러오기 */
const fetchPointAndTicketData = async () => {
  try {
    await Promise.all([
      paymentsStore.getOwnPoint(),
      paymentsStore.fetchTicketCount(),
    ]);
  } catch (e) {
    DEV_MODE && console.error(`낭만궁합 > 포인트 및 분석권 정보 로드 실패: ${JSON.stringify(e)}`);
  }
};

const setupInit = async () => {
  loadingPersonalityInfo.value = true;
  try {
    const typeId = ANALYSIS_SERVICE_CONFIG.nangmanHarmony.typeId;
    await Promise.all([
      fetchPointAndTicketData(),
      fetchAnalysisLogData(typeId),
      fetchAllContents(),
    ]);
  } catch (e) {
    DEV_MODE && console.error('낭만궁합 > 초기화 실패:', e);
  } finally {
    loadingPersonalityInfo.value = false;
  }
};

const ready = ref(false);
const restoringHistory = ref(false);

const restoreHistory = async (id) => {
  if (!id || !context.isActive()) return;
  restoringHistory.value = true;
  store.setHarmonyPurchaseState(false, null);
  store.resetHarmonyPaymentData();
  dataStore.setHarmonyData({});
  try {
    await fetchAnalysisLogData(ANALYSIS_SERVICE_CONFIG.nangmanHarmony.typeId);
    if (!context.isActive() || String(route.query.analysisLogId) !== String(id)) return;
    const logs = store.analysisLogByType[ANALYSIS_SERVICE_CONFIG.nangmanHarmony.typeId] || [];
    let log = logs.find((item) => String(item.analysisLogId) === String(id));
    if (!log) {
      const history = await getAnalysisLogAPI({ analysisTypeId: 38, isValid: true, page: 1, size: -1 });
      if (!context.isActive() || String(route.query.analysisLogId) !== String(id)) return;
      log = history?.content?.find((item) => String(item.analysisLogId) === String(id));
    }
    if (!log?.members?.length) throw new Error('궁합 이용내역을 찾을 수 없습니다.');
    const baseId = getHarmonyBaseId(log);
    if (baseId !== Number(props.saju.guestUserId)) {
      context.openHistory('harmony', baseId, id);
      return;
    }
    const members = [...log.members].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    store.setHarmonyPaymentData({
      category: log.category,
      memberGuestUserIds: members.map((m) => Number(m.guestUserId)),
      persons: members,
    });
    store.setHarmonyPurchaseState(true, log);
    store.clickMainMenu('/nangman-harmony');
  } catch (error) {
    if (!context.isActive() || String(route.query.analysisLogId) !== String(id)) return;
    showAlert({ title: error.message || '궁합 이용내역을 불러오지 못했습니다.' });
    store.clickMainMenu('/overviewNangmanHarmony');
  } finally {
    if (String(route.query.analysisLogId) === String(id)) restoringHistory.value = false;
  }
};

watch(() => store.harmonyMatchedLog, (log) => {
  if (!ready.value || !context.isActive() || selectedMenu.value !== '/nangman-harmony') return;
  if (getHarmonyBaseId(log) !== Number(props.saju.guestUserId) || !log?.analysisLogId) return;
  if (String(route.query.analysisLogId) !== String(log.analysisLogId)) {
    context.openHistory('harmony', props.saju.guestUserId, log.analysisLogId);
  }
});

onMounted(async () => {
  await setupInit();
  if (!context.isActive()) return;
  ready.value = true;
  if (route.query.analysisLogId) await restoreHistory(route.query.analysisLogId);
  else if (selectedMenu.value === '/nangman-harmony' && !store.harmonyPaymentData) {
    store.clickMainMenu('/overviewNangmanHarmony');
  }
});

watch(() => route.query.analysisLogId, (id) => {
  if (!id) restoringHistory.value = false;
  if (ready.value && id) restoreHistory(id);
});

/** 상세분석 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    try {
      await setupInit();
      if (!context.isActive()) return;
      componentKey.value += 1;
    } catch (error) {
      DEV_MODE && console.error('결제 후 정보 갱신 중 오류:', error);
    }
  }
};

watch(selectedMenu, () => { toggleTabOpenClass.value = ''; });

const currentHarmonyLogs = computed(() =>
  (serviceData.nangmanHarmony?.logData?.value || []).filter((log) =>
    getHarmonyBaseId(log) === Number(props.saju.guestUserId)),
);

// 낭만궁합 전용 computed
const harmonyMainView = computed(() => {
  const key = activeServiceKey.value;
  if (key !== 'nangmanHarmony') return null;

  const cfg = ANALYSIS_SERVICE_CONFIG.nangmanHarmony;
  const menu = selectedMenu.value;
  const isOverview = menu.startsWith(cfg.overviewPath);
  const isDetail = menu.startsWith(cfg.detailPath);
  if (!isOverview && !isDetail) return null;

  if (isOverview) {
    return {
      component: CoverPageHarmony,
      props: {
        ticketCountData: serviceData.nangmanHarmony?.ticketData?.value,
        hasPermission: serviceData.nangmanHarmony?.permission?.value,
        harmonyLogData: currentHarmonyLogs.value,
        loadingTicket: loadingPersonalityInfo.value,
      },
    };
  }

  return {
    component: DetailPageHarmony,
    props: {
      hasPermission: serviceData.nangmanHarmony?.permission?.value,
    },
  };
});

const harmonyOverviewPanel = computed(() => {
  const key = activeServiceKey.value;
  if (key !== 'nangmanHarmony') return null;

  const cfg = ANALYSIS_SERVICE_CONFIG.nangmanHarmony;
  const menu = selectedMenu.value;
  const isOverview = menu.startsWith(cfg.overviewPath);
  const isDetail = menu.startsWith(cfg.detailPath);

  if (isOverview || (isDetail && tendencyGuide.value)) {
    return {
      component: CoverPanelHarmony,
      props: {
        harmonyTicketData: serviceData.nangmanHarmony?.ticketData?.value,
        harmonyLogData: currentHarmonyLogs.value,
        hasPermission: serviceData.nangmanHarmony?.permission?.value,
      },
    };
  }
  return null;
});

const harmonyDetailPanel = computed(() => {
  const key = activeServiceKey.value;
  if (key !== 'nangmanHarmony') return null;

  const cfg = ANALYSIS_SERVICE_CONFIG.nangmanHarmony;
  const menu = selectedMenu.value;
  if (!menu.startsWith(cfg.detailPath) || tendencyGuide.value) return null;

  return {
    component: DetailPanelHarmony,
    props: {
      harmonyTicketData: serviceData.nangmanHarmony?.ticketData?.value,
      harmonyLogData: currentHarmonyLogs.value,
      hasPermission: serviceData.nangmanHarmony?.permission?.value,
    },
  };
});
</script>

<template>
  <!-- 팝업 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="analysisContent"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="analysisContent"
    :ticketCountData="analysisTicketData"
    :ticketCount="analysisTicketCount"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <!-- 메인 콘텐츠 -->
  <div class="analysis-main" :class="toggleTabOpenClass" v-if="harmonyMainView">
    <component
      :is="harmonyMainView.component"
      :key="componentKey"
      v-bind="harmonyMainView.props"
      @goPopAnalysis="showPopAnalysisInfoDo"
    />
  </div>

  <!-- 우측 패널: overview -->
  <div
    :class="['analysis-desc', 'overview-payment-info', toggleTabOpenClass]"
    v-if="harmonyOverviewPanel"
  >
    <component
      :is="harmonyOverviewPanel.component"
      :key="componentKey"
      v-bind="harmonyOverviewPanel.props"
      @goPopAnalysis="showPopAnalysisInfoDo"
    />
  </div>

  <!-- 우측 패널: detail -->
  <div
    :class="['analysis-desc', toggleTabOpenClass]"
    v-if="harmonyDetailPanel"
  >
    <component
      :is="harmonyDetailPanel.component"
      v-bind="harmonyDetailPanel.props"
      @goPopAnalysis="showPopAnalysisInfoDo"
      @payment-success="paymentSuccessDo"
    />
  </div>

  <!-- 토글 버튼 -->
  <div
    class="btn-fold-toggle"
    :class="[toggleTabOpenClass, { 'overview-mode': selectedMenu?.startsWith('/overview') }]"
  >
    <button
      class="btn"
      type="button"
      :aria-expanded="toggleTabOpenClass !== 'wide'"
      :aria-label="toggleTabOpenClass === 'wide' ? '이용 내역 패널 펼치기' : '이용 내역 패널 접기'"
      :title="toggleTabOpenClass === 'wide' ? '이용 내역 패널 펼치기' : '이용 내역 패널 접기'"
      @click="toggleTabContent()"
    ></button>
  </div>

  <AnalysisLoadingOverlay
    :loading="!ready || restoringHistory"
    message="낭만궁합 분석중입니다!"
    contained
  />

  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>
